import {
  ComponentProps,
  forwardRef,
  Fragment,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import ConditionalRenderer from '../ConditionalRenderer';
import TextInput from './TextInput';
import { BaseInputProps } from './BaseInput';
import { twMerge } from 'tailwind-merge';

type SpanInputProps = Omit<ComponentProps<'input'>, 'children' | 'className'> &
  BaseInputProps & {
    isEditing: boolean;
    children?: (value?: string) => React.ReactElement<{ className?: string }>;
    maxLenght?: number;
  };
const SpanInput = forwardRef<HTMLInputElement, SpanInputProps>(
  (
    { value, isEditing, children, className, prefix, maxLenght, ...props },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const textView = children?.(value as string);
    const childrenClassName = textView?.props.className;

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);
    useEffect(() => {
      if (isEditing) inputRef.current?.focus();
    }, [isEditing, value]);

    return (
      <Fragment>
        <ConditionalRenderer condition={!isEditing}>
          {children ? children(value as string) : <span>{value}</span>}
        </ConditionalRenderer>
        <ConditionalRenderer condition={isEditing}>
          <TextInput
            ref={inputRef}
            value={value}
            onFocus={e => e.target.select()}
            {...props}
            type="text"
            maxLenght={maxLenght}
            prefix={prefix}
            className={{
              ...className,
              base: twMerge(
                'bg-transparent border-none w-fit',
                className?.base,
              ),
              input: twMerge(
                childrenClassName,
                'border-neutral-content bg-transparent focus-visible:ring-0 h-fit p-0 px-2 leading-none rounded-md',
                className?.input,
              ),
            }}
            color="primary"
          />
        </ConditionalRenderer>
      </Fragment>
    );
  },
);

export default SpanInput;
